import {
  OrganizationContentModel,
  TermsPolicyDataUseContentModel,
  TermsPolicyDataUseEntry,
} from '@cibo/core'
import { Entry } from 'contentful'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { useEffect, useMemo, useState } from 'react'

import { useOrganizationContent } from '../queries'
import { USER_DATA_KEYS, useUserData } from './index'
import { useAuth } from './useAuth'

dayjs.extend(isSameOrAfter)

interface TermsAgreement {
  date?: Date
  status: 'accepted' | 'revoked' | 'denied'
  version: number
}

interface TermUserData {
  currentAgreement?: TermsAgreement
  pastAgreements?: (TermsAgreement | string)[]
}

type TermsUserData = Record<string, TermUserData>

export const findUncompliantTerms = (
  organizationContent: Entry<OrganizationContentModel>,
  userTermsData?: TermsUserData
) => {
  const termsLikeContent = [
    organizationContent.fields.termsPolicyDataUse,
    organizationContent.fields.privacyPolicy,
  ].filter(termsLike => !!termsLike) as Array<Entry<TermsPolicyDataUseContentModel>>
  if (!userTermsData) {
    return termsLikeContent
  }

  const uncompliantTerms = termsLikeContent.filter(content => {
    const termUserData = userTermsData[content.sys.id]

    // no current agreement
    if (!termUserData) return true

    // wrong version
    if (termUserData.currentAgreement?.version !== content.fields.version) return true

    // not agreed
    if (termUserData.currentAgreement?.status !== 'accepted') return true

    // AOK
    return false
  })

  return uncompliantTerms.length > 0
    ? (uncompliantTerms as Entry<TermsPolicyDataUseContentModel>[])
    : []
}

export const useTermsStatus = () => {
  const { isLoggedIn, authResolved, logout } = useAuth()
  const orgContent = useOrganizationContent()
  const userTermsData = useUserData<any>(USER_DATA_KEYS.TERMS_AND_CONDITIONS)

  const [uncompliantTerms, setUncompliantTerms] =
    useState<Entry<TermsPolicyDataUseContentModel>[] | undefined>()
  const [currentTerms, setCurrentTerms] = useState<Entry<TermsPolicyDataUseContentModel>>()

  const orgTerms = useMemo(
    () =>
      [orgContent.data?.fields.termsPolicyDataUse, orgContent.data?.fields.privacyPolicy].filter(
        termsLike => !!termsLike
      ) as Array<TermsPolicyDataUseEntry>,
    [orgContent.isFetched]
  )

  useEffect(() => {
    if (!orgContent.data) {
      setCurrentTerms(undefined)
      setUncompliantTerms(undefined)
      return
    }

    const newUncompliantTerms = findUncompliantTerms(orgContent.data, userTermsData.data)
    setUncompliantTerms(newUncompliantTerms)

    if (!!newUncompliantTerms?.length) {
      setCurrentTerms(newUncompliantTerms[0])
    }
  }, [orgContent.isFetched, userTermsData.isFetching])

  const modifyTerms = (
    terms: { id: string; version: number },
    date: Date | null,
    status: 'accepted' | 'revoked' | 'declined',
    onSuccess?: () => void
  ) => {
    const newPastAgreements =
      userTermsData.data && userTermsData.data[terms.id]?.currentAgreement
        ? [
            userTermsData.data[terms.id].currentAgreement,
            ...userTermsData.data[terms.id].pastAgreements,
          ]
        : []

    const existingTerms = userTermsData.data || {}

    const newTermsAgreements = {
      ...existingTerms,
      [terms.id]: {
        currentAgreement: { date, status, version: terms.version },
        pastAgreements: newPastAgreements,
      },
    }
    userTermsData.update(newTermsAgreements)?.then(() => onSuccess && onSuccess())
  }

  const confirmTerms = () => {
    currentTerms &&
      modifyTerms(
        { id: currentTerms?.sys.id, version: currentTerms.fields.version },
        new Date(),
        'accepted'
      )
  }
  const declineTerms = () => {
    currentTerms &&
      modifyTerms(
        { id: currentTerms?.sys.id, version: currentTerms.fields.version },
        null,
        'declined',
        () => logout()
      )
  }

  const revokeAcceptance = (terms: Entry<TermsPolicyDataUseContentModel>) => {
    modifyTerms({ id: terms?.sys.id, version: terms.fields.version }, null, 'revoked', () =>
      logout()
    )
  }

  const output = {
    isFetched:
      authResolved && isLoggedIn
        ? userTermsData.isFetched && orgContent.isSuccess
        : orgContent.isSuccess,
    isLoading: userTermsData.isLoading || orgContent.isLoading,
    isError: orgContent.isError || userTermsData.fetchError || !!userTermsData.updateError,
    uncompliantTerms,
    currentTerms,
    currentTermsContent: currentTerms?.fields,
    confirmTerms,
    declineTerms,
    revokeAcceptance,
    orgTerms,
    userTerms: userTermsData.data,
    currentTermPriorAgreeDate:
      currentTerms && userTermsData.data
        ? userTermsData.data[currentTerms?.sys.id]?.currentAgreement?.date
        : undefined,
  }

  return output
}
